/**
 * SunsetBackground Component - Option B: Warmer Sunset with Shooting Stars
 * Deeper oranges, corals, golden amber with elegant shooting star effect
 */
import React, { useEffect, useRef } from 'react';

const SunsetBackground = ({ variant = 'default' }) => {
  const isDark = variant === 'dark';
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;
    
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Static gradient blobs (no movement - just ambient color)
    const blobs = [
      { x: 0.15, y: 0.25, radius: 0.45, color: isDark ? 'rgba(251, 146, 60, 0.12)' : 'rgba(255, 180, 120, 0.45)' },
      { x: 0.75, y: 0.15, radius: 0.4, color: isDark ? 'rgba(251, 113, 133, 0.1)' : 'rgba(255, 160, 140, 0.4)' },
      { x: 0.5, y: 0.65, radius: 0.5, color: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(255, 200, 100, 0.45)' },
      { x: 0.2, y: 0.75, radius: 0.35, color: isDark ? 'rgba(234, 88, 12, 0.08)' : 'rgba(255, 150, 100, 0.35)' },
      { x: 0.85, y: 0.55, radius: 0.38, color: isDark ? 'rgba(217, 119, 6, 0.09)' : 'rgba(255, 190, 130, 0.38)' },
    ];
    
    // Shooting stars configuration
    const shootingStars = [];
    const maxStars = 4; // Max concurrent shooting stars
    
    const createShootingStar = () => {
      const { width, height } = canvas;
      return {
        x: width * 0.3 + Math.random() * width * 0.5, // Start from middle-right area (where sunset gradient is)
        y: Math.random() * height * 0.35, // Start from top 35%
        length: 120 + Math.random() * 180, // Longer trail
        speed: 2 + Math.random() * 2.5, // Slower, more elegant speed
        angle: Math.PI / 4 + Math.random() * Math.PI / 6, // Angle (45-75 degrees) - more diagonal
        opacity: 0,
        fadeIn: true,
        life: 0,
        maxLife: 100 + Math.random() * 60, // Longer life for visibility
        thickness: 2 + Math.random() * 1.5, // Thicker trails
        color: isDark 
          ? `rgba(220, 220, 255, ` 
          : `rgba(255, 180, 120, ` // More visible warm amber color
      };
    };
    
    const updateShootingStars = () => {
      // Spawn stars more frequently
      if (shootingStars.length < maxStars && Math.random() < 0.04) { // Even higher probability
        shootingStars.push(createShootingStar());
      }
      
      // Update existing stars
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const star = shootingStars[i];
        
        // Move star
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;
        star.life++;
        
        // Fade in/out
        if (star.fadeIn && star.opacity < 1) {
          star.opacity += 0.08;
          if (star.opacity >= 1) star.fadeIn = false;
        } else if (star.life > star.maxLife * 0.6) {
          star.opacity -= 0.03;
        }
        
        // Remove dead stars
        if (star.opacity <= 0 || star.life > star.maxLife) {
          shootingStars.splice(i, 1);
        }
      }
    };
    
    const drawShootingStar = (star) => {
      const { width, height } = canvas;
      
      // Calculate trail end point
      const endX = star.x - Math.cos(star.angle) * star.length;
      const endY = star.y - Math.sin(star.angle) * star.length;
      
      // Create gradient for trail
      const gradient = ctx.createLinearGradient(star.x, star.y, endX, endY);
      gradient.addColorStop(0, star.color + (star.opacity * 0.9) + ')');
      gradient.addColorStop(0.3, star.color + (star.opacity * 0.5) + ')');
      gradient.addColorStop(1, star.color + '0)');
      
      // Draw trail
      ctx.beginPath();
      ctx.moveTo(star.x, star.y);
      ctx.lineTo(endX, endY);
      ctx.strokeStyle = gradient;
      ctx.lineWidth = star.thickness;
      ctx.lineCap = 'round';
      ctx.stroke();
      
      // Draw bright head
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.thickness * 1.5, 0, Math.PI * 2);
      ctx.fillStyle = star.color + star.opacity + ')';
      ctx.fill();
      
      // Glow effect
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.thickness * 4, 0, Math.PI * 2);
      const glowGradient = ctx.createRadialGradient(star.x, star.y, 0, star.x, star.y, star.thickness * 4);
      glowGradient.addColorStop(0, star.color + (star.opacity * 0.4) + ')');
      glowGradient.addColorStop(1, star.color + '0)');
      ctx.fillStyle = glowGradient;
      ctx.fill();
    };
    
    const drawBlob = (blob) => {
      const { width, height } = canvas;
      
      const x = blob.x * width;
      const y = blob.y * height;
      const radius = blob.radius * Math.min(width, height);
      
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, blob.color);
      gradient.addColorStop(0.6, blob.color.replace(/[\d.]+\)$/, (parseFloat(blob.color.match(/[\d.]+\)$/)[0]) * 0.4) + ')'));
      gradient.addColorStop(1, 'transparent');
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
    };
    
    const drawBackground = () => {
      const { width, height } = canvas;
      
      const baseGradient = ctx.createLinearGradient(0, 0, width * 0.3, height);
      if (isDark) {
        baseGradient.addColorStop(0, '#1a1a2e');
        baseGradient.addColorStop(1, '#16213e');
      } else {
        baseGradient.addColorStop(0, '#fef9f3');
        baseGradient.addColorStop(0.4, '#fdf5ed');
        baseGradient.addColorStop(0.7, '#fcf0e5');
        baseGradient.addColorStop(1, '#f9ebdd');
      }
      
      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, width, height);
    };
    
    const drawSubtleGrid = () => {
      if (isDark) return;
      
      const { width, height } = canvas;
      ctx.strokeStyle = 'rgba(220, 180, 140, 0.05)';
      ctx.lineWidth = 1;
      
      const gridSize = 80;
      
      for (let x = 0; x < width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      for (let y = 0; y < height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
    };
    
    const animate = () => {
      time += 1;
      
      drawBackground();
      
      // Draw static blobs
      ctx.globalCompositeOperation = 'multiply';
      blobs.forEach(blob => drawBlob(blob));
      ctx.globalCompositeOperation = 'source-over';
      
      drawSubtleGrid();
      
      // Update and draw shooting stars
      updateShootingStars();
      shootingStars.forEach(star => drawShootingStar(star));
      
      animationId = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, [isDark]);
  
  return (
    <div className="fixed inset-0 -z-10 overflow-hidden" data-testid="sunset-background">
      <canvas 
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ display: 'block' }}
      />
      
      <div 
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />
      
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: isDark 
            ? 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.3) 100%)'
            : 'radial-gradient(ellipse at center, transparent 55%, rgba(200,150,100,0.08) 100%)'
        }}
      />
    </div>
  );
};

export { SunsetBackground };
export default SunsetBackground;
